import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";

import parse, { HTMLReactParserOptions } from "html-react-parser";

const enum fields {
    links = "links",
    tabType = "tabType",
    destination = "destination",
    activeTab = "activeTab"
}

export interface LinkModel {
    fields: {
        destination: LinkFieldValue;
        overriddenTitle: string;
        backgroundColor: string;
        textColor?: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.tabType]:  "tab" | "link";
        [fields.activeTab] : string;
    };
}

interface TabComponentStyles {
    wrapper?: InjectableCss;
    tabs?: InjectableCss;
    tabButton?: ButtonPresentationProps;
    tabContent?: InjectableCss;
    buttonBackDrop?: InjectableCss;
    buttonBackDropText?: InjectableCss;
}

export const tabComponentStyles: TabComponentStyles = {
    tabs: {
        css: css`
            margin-top: -90px !important;
            display: flex;
            flex-wrap: wrap;
            max-width: 1300px;
            margin: auto;   
            gap:15px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    css`
                        flex-direction: column;
                        gap: 5px;
                        padding: 10px;
                        margin-top: -40px !important;
                    `,
                    null,
                    null,
                    null,
                ],"max");
            }}
        `,
    },
    tabButton: {
        css: css`
            flex-grow: 1;
            border: none !important;
            position: relative;
            border-top-right-radius: 5px;
            border-top-left-radius: 5px;
            cursor: pointer;
            background: transparent;
            *{
           
                line-height: normal;
            }
            line-height: 20px;
            text-align: left;
            padding: 25px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    css`
                        text-align: left;
                        border-radius: 0px !important;
                        div{
                            border-radius: 0px !important;
                        }
                    `,
                    null,
                    null,
                    null,
                ],"max");
            }}
            &:hover {
              
                *{
                
                }
            }
            &.active {
              
               
                *{
               
                }
            }
        `,
    },
    tabContent: {
        css: css`
            padding: 20px 25px;
        `,
    },
    buttonBackDrop:{
        css:css`
            opacity: 1;
            width: 100%;
            height: 100%;
            left: 0px;
            top: 0px;
            position: absolute;
            z-index: 0;
            border-top-right-radius: 5px;
            border-top-left-radius: 5px;

        `
    },
    buttonBackDropText:{
        css:css`
            position: absolute;
            z-index: 1;
            top: 0px;
            left: 0px;
            padding: 15px;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            font-size: 16px;
            font-weight: 600;
        `
    }
};

const styles = tabComponentStyles;

type Props = OwnProps;

const EsgDynamicTab: React.FC<Props> = ({ id, fields }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const [activeTab, setActiveTab] = useState(fields?.activeTab && fields?.tabType === "link" ? Number(fields?.activeTab) - 1: 0);


    const handleTabClick = (index: number) => {
        setActiveTab(index);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper className="tabs" {...styles.tabs}>
                {links.links.map((link:any, index:number) => (
                    <Button
                        {...styles.tabButton}
                        key={index}
                        className={`tab-button ${activeTab === index ? "active" : ""}`}
                        onClick={() => {
                            if(fields?.tabType === "link" && link.url){
                                window.location.href = link.url;
                            } else {
                                handleTabClick(index);
                            }
                        }

                        }
                    >
                    <StyledWrapper 
                        style={{
                            opacity: activeTab === index ? 1:0.8,
                            width: "100%",
                            height: "100%",
                            left: "0px",
                            top: "0px",
                            position: "absolute",
                            zIndex: 0,
                            borderTopRightRadius: "5px",
                            borderTopLeftRadius: "5px",
                            backgroundColor: fields?.links[index].fields?.backgroundColor,
                        }}
                    >

                    </StyledWrapper>
                    <StyledWrapper 
                     style={{
                        position: "absolute",
                        zIndex: 1,
                        top: "0px",
                        left: "0px",
                        padding: "15px",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        fontSize: "16px",
                        fontWeight: 600,
                        color:fields?.links[index]?.fields?.textColor
                     }}
                    >
                        {parse(fields?.links[index]?.fields?.overriddenTitle, parserOptions)} 
                    </StyledWrapper>
                    
                    </Button>
                ))}
            </StyledWrapper>
            <StyledWrapper className="tab-content" 
                style={{
                    padding: "padding: 20px 25px;",
                    background:`linear-gradient(to bottom, ${fields?.links[activeTab]?.fields?.backgroundColor} 0%, white 33%)`
                }}
            >
                {fields?.tabType === "link" ?
                    <Zone contentId={id} zoneName="linkContent" />
                    :
                    <Zone contentId={id} zoneName={fields?.links[activeTab]?.fields?.overriddenTitle} />
                }
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: EsgDynamicTab,
    definition: {
        group: "Basic",
        displayName: "ESG Dynamic tab",
        fieldDefinitions: [
            {
                name: fields.tabType,
                displayName: "Tab type",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Tab", value: "tab" },
                    { displayName: "Link", value: "link" },
                ],
                defaultValue: "tab",
                hideEmptyOption: true,
            },
            {
                name: fields.activeTab,
                fieldType: "General",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "1",
                isVisible: widget => widget.fields.tabType === "link",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: item => {
                    const {
                        destination: { value },
                        overriddenTitle,
                    } = item.fields;
                    return parse(overriddenTitle, parserOptions) || value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "textColor",
                        displayName: "Text Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.destination,
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { value: "", type: "Page" }, 
                    },
                    {
                        name: "backgroundColor",
                        displayName: "Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                ],
                tab: basicTab,
            },
            
            
        ],
    },
};

export default widgetModule;
